import { EnumButtonName } from '@calm/evexia-analytics';
import { MediaProgramGuide } from '@calm/evexia-web-sdk';
import { FavoriteGuideFn } from '@rhg/hooks/use-favorite-guide';
import commonMessages from '@rhg/lang/messages/common';
import { IconButton, IconButtonProps } from '@rhg/libs/material';
import {
    HeartFilledToggle,
    HeartOutlineToggle,
} from '@rhg/libs/material/icons';
import { sxAppend } from '@rhg/libs/material/sx';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

type FavoriteButtonProps = {
    trackButtonFavorite: EnumButtonName;
    trackButtonUnfavorite: EnumButtonName;
    guide: MediaProgramGuide;
    prefersDarkToast?: boolean;
    favoriteGuide: FavoriteGuideFn;
    canFavorite: boolean;
} & Omit<IconButtonProps, 'trackButton' | 'onClick'>;

export function FavoriteIcon({ isFavorite }: { isFavorite?: boolean }) {
    return isFavorite ? <HeartFilledToggle /> : <HeartOutlineToggle />;
}

const styleNoHover = {
    ':hover': {
        backgroundColor: 'transparent',
    },
};

const styleFavoriteButton = (isFavorite?: boolean) => ({
    color: isFavorite ? 'secondary.main' : 'action.active',
});

export function FavoriteButton({
    guide,
    trackButtonFavorite,
    trackButtonUnfavorite,
    prefersDarkToast,
    sx,
    favoriteGuide,
    canFavorite,
    ...props
}: FavoriteButtonProps) {
    const { formatMessage } = useIntl();

    const shouldHide = !canFavorite;

    const handleFavoriteGuide = useCallback(() => {
        if (shouldHide) {
            return;
        }

        favoriteGuide(guide, !guide.favorite, prefersDarkToast);
    }, [guide, favoriteGuide, prefersDarkToast, shouldHide]);

    if (shouldHide) {
        return null;
    }

    return (
        <IconButton
            trackButton={
                guide?.favorite ? trackButtonUnfavorite : trackButtonFavorite
            }
            onClick={handleFavoriteGuide}
            aria-label={
                guide?.favorite
                    ? formatMessage(commonMessages['common.unfavorite'], {
                          title: guide?.title,
                      })
                    : formatMessage(
                          commonMessages['common.favorite_with_title'],
                          {
                              title: guide?.title,
                          }
                      )
            }
            sx={sxAppend(styleNoHover, styleFavoriteButton(guide.favorite), sx)}
            {...props}
        >
            <FavoriteIcon isFavorite={guide?.favorite} />
        </IconButton>
    );
}
